import {
  AVATAR_COLORS,
  BOUNCE_SPEED,
  DURATION_SECONDS,
  FIELD,
  FRUIT_KILL_X,
  FRUIT_SPAWN_X,
  FROZEN_GROUND_Y,
  FROZEN_JUMP_SPEED,
  FROZEN_GRAVITY,
  FROZEN_PLAYER_SPEED,
  FROZEN_PLAYER_WIDTH,
  STUN_MS,
  VASO_INNER,
  VASO_POINTS,
  chainValue,
  passSpeedAt,
  frozenKind,
  frozenPlan,
  stomps,
  type FrozenKind,
  type FrozenSpawn,
} from "@clubap/shared";
import { Application, Container, Graphics, Sprite, Text, Texture } from "pixi.js";
import { loadArt } from "../art/loader.js";
import { createAvatar, type AvatarView } from "./avatar.js";

/**
 * El puesto del Frozen de Frutas: dueño del canvas de UNA partida.
 *
 * Mismo reparto que `RoomStage` con `App.tsx`, y por el mismo motivo: acá viven el bucle
 * de 60 fotogramas y todo lo que muta cada uno de ellos, y React se queda con el marco,
 * los botones y el envío del puntaje. Un estado de React que cambie sesenta veces por
 * segundo vuelve a montar el árbol sesenta veces por segundo, que en este repo ya costó
 * 97 reconexiones una vez.
 *
 * El avatar NO se dibuja acá: se pide a `createAvatar`, el mismo que usa el campus. Es
 * lo que hace que saltes con tu color y con la gorra que te compraste sin que este
 * archivo sepa qué es una gorra.
 */

/** Lo que la UI necesita saber y cambia POCAS veces por segundo. */
export interface FrozenHud {
  score: number;
  /** Segundos que quedan, ya redondeados: la UI no necesita más resolución. */
  remaining: number;
  /** Pisadas encadenadas sin tocar el suelo. 0 con los pies en el suelo. */
  chain: number;
  /** Vasos servidos. Es el marcador de sabor. */
  vasos: number;
  /** Ardiendo por un ají: la UI lo dice, porque el avatar solo no lo explica. */
  stunned: boolean;
}

export type FrozenAction = "izquierda" | "derecha" | "salto";

export interface FrozenStageOptions {
  /** Índice en `AVATAR_COLORS`, igual que en el estado de la sala. */
  color: number;
  /** Lo que el jugador lleva puesto. Se dibuja igual que en el campus. */
  equipped: readonly string[];
  onHud: (hud: FrozenHud) => void;
  /** La partida terminó. El puntaje lo envía React, que es quien tiene la sesión. */
  onEnded: (score: number) => void;
}

/** Una fruta viva. */
interface Passing {
  x: number;
  y: number;
  kind: FrozenKind;
  sprite: Container;
  /** Rueda mientras cruza: una fruta que se desliza rígida se ve pegada al fondo. */
  spin: number;
}

/** Un salpicón o un "+3": se dibuja, se apaga y se va. */
interface Effect {
  node: Container;
  life: number;
  ttl: number;
  riseSpeed: number;
  growth: number;
}

const EFFECT_TTL = 0.42;

/** Colores del puesto. La misma paleta apagada que el fondo de la FIEC Vieja. */
const SKY_TOP = 0xd8dcc8;
const SKY_BOTTOM = 0xefe7d6;
const FLOOR = 0xd7b98a;
const FLOOR_EDGE = 0xb99a6e;
const OUTLINE = 0x2b2b2b;

export class FrozenStage {
  private app = new Application();
  private destroyed = false;

  private world = new Container();
  private fruitLayer = new Container();
  private effectLayer = new Container();

  private avatar: AvatarView | null = null;
  private pieces = new Map<string, Texture>();

  /** El relleno del vaso. Se redibuja cuando cambia, no cada fotograma. */
  private vasoFill = new Graphics();
  private vasoBox = { x: 0, y: 0, width: 0, height: 0 };
  private vasoDrawn = -1;

  private plan: FrozenSpawn[] = [];
  private cursor = 0;
  private passing: Passing[] = [];
  private effects: Effect[] = [];

  /* El cuerpo. `y` son los PIES, igual que en el campus: `createAvatar` ancla ahí. */
  private body = { x: FIELD.width / 2, y: FROZEN_GROUND_Y, previousY: FROZEN_GROUND_Y, vy: 0 };
  private onGround = true;
  private stunUntil = 0;

  /* Hacia dónde mira y si camina: se derivan del movimiento y los lee `avatar.update`. */
  private facing: "s" | "w" | "e" = "s";
  private walking = false;

  private held = { izquierda: false, derecha: false };
  private jumpQueued = false;

  private elapsed = 0;
  private score = 0;
  private chain = 0;
  private vasos = 0;
  private vasoPoints = 0;
  private running = false;

  /** Lo último que se le contó a React, para no contárselo otra vez igual. */
  private lastHud = "";

  private constructor(
    private readonly container: HTMLElement,
    private readonly options: FrozenStageOptions,
  ) {}

  static async create(
    container: HTMLElement,
    options: FrozenStageOptions,
  ): Promise<FrozenStage> {
    const stage = new FrozenStage(container, options);
    await stage.init();
    return stage;
  }

  private async init(): Promise<void> {
    const art = await loadArt();

    await this.app.init({
      background: SKY_BOTTOM,
      antialias: true,
      width: FIELD.width,
      height: FIELD.height,
      resolution: Math.min(window.devicePixelRatio, 2),
      autoDensity: true,
    });

    // `create()` puede resolverse después de que el panel se cerró.
    if (this.destroyed) {
      this.app.destroy({ removeView: true, releaseGlobalResources: false });
      return;
    }

    /* El canvas se estira por CSS al ancho del panel y el lienzo se queda en 640x360.
       Es lo mismo que hace la cancha de volley: una sola resolución lógica para la
       física, y el escalado como problema de la hoja de estilos. */
    this.app.canvas.style.width = "100%";
    this.app.canvas.style.height = "auto";
    this.app.canvas.style.display = "block";
    this.container.appendChild(this.app.canvas);

    this.pieces = art.games.get("frozen") ?? new Map();

    this.world.addChild(this.buildScenery());
    this.world.addChild(this.fruitLayer);

    /* El vaso va DELANTE de la fruta y detrás del avatar. Está al final del recorrido,
       así que lo que se te escapa desaparece entrándole por la boca en vez de cruzarlo
       como un fantasma — es la única pista de que lo perdiste, y sale gratis. */
    this.world.addChild(this.buildVaso());

    const avatar = createAvatar({
      color: AVATAR_COLORS[this.options.color] ?? AVATAR_COLORS[0],
      nickname: "",
      layers: art.avatarLayers,
      itemLayers: art.itemLayers,
      isSelf: false,
    });

    /* El rótulo y el globo se quedan fuera a propósito: `createAvatar` los devuelve en
       un contenedor suelto justo para que quien lo monta decida, y en una pantalla de
       un solo jugador tu propio nombre flotando sobre tu cabeza es ruido. */
    avatar.setEquipped([...this.options.equipped]);
    this.world.addChild(avatar.root);
    this.avatar = avatar;

    this.world.addChild(this.effectLayer);
    this.app.stage.addChild(this.world);

    this.placeAvatar();
    this.bindKeys();

    this.app.ticker.add(() => this.tick(this.app.ticker.deltaMS));
  }

  /**
   * El decorado de fondo: cielo y piso. El vaso NO — ver dónde se monta.
   *
   * Con primitivas y no con un fondo dibujado, igual que la cancha de volley y por el
   * mismo motivo: el arte lateral del campus todavía no existe, y un fondo pintado a
   * medias envejece peor que cuatro rectángulos honestos en la paleta correcta.
   */
  private buildScenery(): Container {
    const scenery = new Container();

    const sky = new Graphics();
    sky.rect(0, 0, FIELD.width, FROZEN_GROUND_Y).fill(SKY_BOTTOM);
    sky.rect(0, 0, FIELD.width, FROZEN_GROUND_Y * 0.55).fill({ color: SKY_TOP, alpha: 0.85 });
    scenery.addChild(sky);

    const floor = new Graphics();
    floor.rect(0, FROZEN_GROUND_Y, FIELD.width, FIELD.height - FROZEN_GROUND_Y).fill(FLOOR);
    floor
      .moveTo(0, FROZEN_GROUND_Y)
      .lineTo(FIELD.width, FROZEN_GROUND_Y)
      .stroke({ width: 4, color: OUTLINE });

    // Baldosas del patio, para que correr se note. Sin ellas el piso es un bloque liso
    // y el avatar parece deslizarse en el sitio.
    for (let x = 0; x <= FIELD.width; x += 64) {
      floor
        .moveTo(x, FROZEN_GROUND_Y)
        .lineTo(x - 18, FIELD.height)
        .stroke({ width: 2, color: FLOOR_EDGE });
    }

    scenery.addChild(floor);

    return scenery;
  }

  /**
   * El vaso del carrito, en la esquina, con su relleno por detrás.
   *
   * El relleno es un rectángulo recortado al hueco que declara `VASO_INNER` en
   * fracciones de la caja del dibujo. Ese reparto —el nivel en código, la forma en el
   * SVG— es lo que deja cambiar el vaso por otro sin tocar una línea de esto.
   */
  private buildVaso(): Container {
    const holder = new Container();

    const texture = this.pieces.get("vaso");
    const height = 116;
    const width = texture ? (texture.width / texture.height) * height : 84;

    /* A la IZQUIERDA: las frutas entran por la derecha y salen por acá. Tenerlo al otro
       extremo lo dejaba justo donde nacen, asomando por detrás de él. */
    const x = 18;
    const y = FROZEN_GROUND_Y - height + 6;

    this.vasoBox = {
      x: x + width * VASO_INNER.left,
      y: y + height * VASO_INNER.top,
      width: width * (VASO_INNER.right - VASO_INNER.left),
      height: height * (VASO_INNER.bottom - VASO_INNER.top),
    };

    holder.addChild(this.vasoFill);

    if (texture) {
      const sprite = new Sprite(texture);
      sprite.width = width;
      sprite.height = height;
      sprite.x = x;
      sprite.y = y;
      holder.addChild(sprite);
    } else {
      // Respaldo: sin el SVG el juego se juega igual, sólo que el vaso es una caja.
      const box = new Graphics();
      box
        .rect(x, y, width, height)
        .stroke({ width: 4, color: OUTLINE });
      holder.addChild(box);
    }

    return holder;
  }

  /** Repinta el nivel del puré. Sólo cuando cambia: es un `clear()` y un `rect()`. */
  private drawVaso(): void {
    const filled = this.vasoPoints / VASO_POINTS;
    if (Math.abs(filled - this.vasoDrawn) < 0.01) return;

    this.vasoDrawn = filled;
    this.vasoFill.clear();

    if (filled <= 0) return;

    const height = this.vasoBox.height * Math.min(filled, 1);
    this.vasoFill
      .rect(
        this.vasoBox.x,
        this.vasoBox.y + this.vasoBox.height - height,
        this.vasoBox.width,
        height,
      )
      .fill(0xc2564e);
  }

  // ------------------------------------------------------------------------
  //  Entrada
  // ------------------------------------------------------------------------

  /**
   * Una intención del jugador. La usan el teclado y los botones de la pantalla.
   *
   * El salto se ENCOLA en vez de leerse como una tecla sostenida: pulsarlo un fotograma
   * antes de aterrizar tenía que contar, o encadenar en el aire se convierte en un
   * concurso de pulsar en el milisegundo exacto en el que los pies tocan.
   */
  press(action: FrozenAction, down: boolean): void {
    if (action === "salto") {
      if (down) this.jumpQueued = true;
      return;
    }

    this.held[action] = down;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.keyAction(event.key, true)) event.preventDefault();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (this.keyAction(event.key, false)) event.preventDefault();
  };

  private keyAction(key: string, down: boolean): boolean {
    const lower = key.toLowerCase();

    if (key === "ArrowLeft" || lower === "a") {
      this.press("izquierda", down);
      return true;
    }
    if (key === "ArrowRight" || lower === "d") {
      this.press("derecha", down);
      return true;
    }
    // La barra y la flecha arriba saltan. `preventDefault` en las dos, o la barra
    // desplaza la página por debajo del panel mientras se juega.
    if (key === " " || key === "ArrowUp" || lower === "w") {
      this.press("salto", down);
      return true;
    }

    return false;
  }

  private bindKeys(): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  // ------------------------------------------------------------------------
  //  La partida
  // ------------------------------------------------------------------------

  /** Arranca una partida con la semilla que dio el servidor. */
  start(seed: number): void {
    this.plan = frozenPlan(seed);
    this.cursor = 0;

    for (const fruit of this.passing) fruit.sprite.destroy({ children: true });
    this.passing = [];
    for (const effect of this.effects) effect.node.destroy({ children: true });
    this.effects = [];

    this.body = { x: FIELD.width / 2, y: FROZEN_GROUND_Y, previousY: FROZEN_GROUND_Y, vy: 0 };
    this.onGround = true;
    this.stunUntil = 0;
    this.held.izquierda = false;
    this.held.derecha = false;
    this.jumpQueued = false;

    this.elapsed = 0;
    this.score = 0;
    this.chain = 0;
    this.vasos = 0;
    this.vasoPoints = 0;
    this.lastHud = "";
    this.running = true;

    this.placeAvatar();
    this.drawVaso();
    this.publish();
  }

  private tick(deltaMs: number): void {
    // Los efectos siguen corriendo con la partida terminada: el último salpicón tiene
    // que apagarse aunque el reloj ya esté en cero.
    const dt = Math.min(deltaMs, 50) / 1000;

    this.stepEffects(dt);

    if (!this.running) return;

    /*
     * El reloj se lleva sumando pasos RECORTADOS, no con la hora de pared.
     *
     * Recortar es obligatorio: una pestaña que vuelve del fondo entrega un `deltaMS`
     * enorme y sin tope el avatar aparecería al otro lado del lienzo y las frutas
     * atravesarían el suelo en un fotograma.
     *
     * El efecto secundario es que una partida estrangulada dura MÁS de 45 segundos de
     * pared, y eso es seguro en la dirección que importa: el servidor divide el puntaje
     * entre el tiempo real transcurrido, así que un reloj de juego que se atrasa sólo
     * puede bajar ese cociente, nunca subirlo. Al revés —usar la hora de pared— la
     * partida se acortaría y el cociente subiría hasta chocar con `maxPointsPerSecond`
     * por haber tenido la mala suerte de cambiar de pestaña.
     */
    this.elapsed += dt;

    if (this.elapsed >= DURATION_SECONDS) {
      this.running = false;
      this.publish();
      this.options.onEnded(this.score);
      return;
    }

    this.spawn();
    this.stepBody(dt);
    this.stepFruit(dt);
    this.collide();
    this.placeAvatar();
    this.drawVaso();
    this.publish();
  }

  private spawn(): void {
    while (this.cursor < this.plan.length) {
      const entry = this.plan[this.cursor];
      if (!entry || entry.at > this.elapsed) break;

      this.cursor += 1;

      const kind = frozenKind(entry.kind);
      if (!kind) continue;

      this.passing.push({
        x: FRUIT_SPAWN_X,
        y: entry.y,
        kind,
        sprite: this.buildFruit(kind),
        spin: 0,
      });
    }
  }

  private buildFruit(kind: FrozenKind): Container {
    const texture = this.pieces.get(kind.id);
    const size = kind.radius * 2;

    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.width = size;
      sprite.height = size;
      this.fruitLayer.addChild(sprite);
      return sprite;
    }

    // Sin dibujo, un círculo del color de la fruta. El juego nunca se queda sin nada
    // que mostrar, que es la misma regla que en el campus.
    const circle = new Graphics();
    circle.circle(0, 0, kind.radius).fill(kind.tint).stroke({ width: 3, color: OUTLINE });
    this.fruitLayer.addChild(circle);
    return circle;
  }

  private stepBody(dt: number): void {
    const stunned = this.elapsed * 1000 < this.stunUntil;

    let vx = 0;
    if (!stunned) {
      if (this.held.izquierda) vx -= FROZEN_PLAYER_SPEED;
      if (this.held.derecha) vx += FROZEN_PLAYER_SPEED;
    }

    this.body.x += vx * dt;

    const half = FROZEN_PLAYER_WIDTH / 2;
    this.body.x = Math.min(Math.max(this.body.x, half), FIELD.width - half);

    if (this.jumpQueued) {
      this.jumpQueued = false;
      if (this.onGround && !stunned) {
        this.body.vy = -FROZEN_JUMP_SPEED;
        this.onGround = false;
      }
    }

    this.body.vy += FROZEN_GRAVITY * dt;
    this.body.previousY = this.body.y;
    this.body.y += this.body.vy * dt;

    if (this.body.y >= FROZEN_GROUND_Y) {
      this.body.y = FROZEN_GROUND_Y;
      this.body.vy = 0;
      this.onGround = true;
      // Tocar el suelo corta la cadena. Es lo único que la corta, y es lo que convierte
      // "seguir en el aire" en la decisión del juego.
      this.chain = 0;
    }

    this.facing = vx === 0 ? "s" : vx < 0 ? "w" : "e";
    this.walking = vx !== 0 && this.onGround;
    this.avatar?.update(this.facing, this.walking, dt);
  }

  private stepFruit(dt: number): void {
    const speed = passSpeedAt(this.elapsed);

    for (const fruit of this.passing) {
      fruit.x -= speed * dt;

      /* Rueda, y rueda en el sentido correcto: va hacia la izquierda, así que gira en
         contra del reloj. Al revés se ve como patinando, que es de esas cosas que nadie
         sabe nombrar pero todo el mundo nota. */
      fruit.spin -= (speed / fruit.kind.radius) * dt;

      fruit.sprite.x = fruit.x;
      fruit.sprite.y = fruit.y;
      fruit.sprite.rotation = fruit.spin;
    }

    // Lo que se escapó por la izquierda se pierde. Sin aviso: el vaso a medio llenar ya
    // lo dice.
    this.passing = this.passing.filter((fruit) => {
      if (fruit.x + fruit.kind.radius >= FRUIT_KILL_X) return true;
      fruit.sprite.destroy({ children: true });
      return false;
    });
  }

  private collide(): void {
    const survivors: Passing[] = [];

    for (const fruit of this.passing) {
      if (!stomps(this.body, { x: fruit.x, y: fruit.y, radius: fruit.kind.radius })) {
        survivors.push(fruit);
        continue;
      }

      this.smash(fruit);
      fruit.sprite.destroy({ children: true });
    }

    this.passing = survivors;
  }

  private smash(fruit: Passing): void {
    this.splash(fruit);

    if (fruit.kind.hazard) {
      /* El ají: rebote corto, cadena a cero y plantado. No quita puntos ni tiempo —
         `STUN_MS` explica por qué—, así que el castigo es lo que caiga mientras tanto. */
      this.body.vy = -BOUNCE_SPEED * 0.45;
      this.onGround = false;
      this.chain = 0;
      this.stunUntil = this.elapsed * 1000 + STUN_MS;
      return;
    }

    this.chain += 1;
    const points = chainValue(fruit.kind.points, this.chain);
    this.score += points;

    this.vasoPoints += points;
    while (this.vasoPoints >= VASO_POINTS) {
      this.vasoPoints -= VASO_POINTS;
      this.vasos += 1;
    }

    this.popup(fruit, points);

    this.body.vy = -BOUNCE_SPEED;
    this.onGround = false;
  }

  private splash(fruit: Passing): void {
    const texture = this.pieces.get("salpicon");
    const size = fruit.kind.radius * 3;

    let node: Container;

    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.width = size;
      sprite.height = size;
      // Un solo dibujo en blanco teñido con el color de la fruta. Ver `FrozenKind.tint`.
      sprite.tint = fruit.kind.tint;
      node = sprite;
    } else {
      const blob = new Graphics();
      blob.circle(0, 0, fruit.kind.radius * 1.4).fill(fruit.kind.tint);
      node = blob;
    }

    node.x = fruit.x;
    node.y = fruit.y;
    this.effectLayer.addChild(node);

    this.effects.push({ node, life: 0, ttl: EFFECT_TTL, riseSpeed: 0, growth: 1.6 });
  }

  private popup(fruit: Passing, points: number): void {
    const text = new Text({
      text: this.chain > 1 ? `+${points} ×${this.chain}` : `+${points}`,
      style: {
        fontFamily: "Verdana, Geneva, sans-serif",
        fontSize: 15,
        fontWeight: "bold",
        fill: this.chain > 1 ? 0xffd75e : 0xffffff,
        stroke: { color: OUTLINE, width: 4 },
      },
    });

    text.anchor.set(0.5, 1);
    text.x = fruit.x;
    text.y = fruit.y - fruit.kind.radius;
    this.effectLayer.addChild(text);

    this.effects.push({ node: text, life: 0, ttl: EFFECT_TTL * 1.6, riseSpeed: 46, growth: 1 });
  }

  private stepEffects(dt: number): void {
    const survivors: Effect[] = [];

    for (const effect of this.effects) {
      effect.life += dt;

      if (effect.life >= effect.ttl) {
        effect.node.destroy({ children: true });
        continue;
      }

      const t = effect.life / effect.ttl;
      effect.node.alpha = 1 - t * t;
      effect.node.y -= effect.riseSpeed * dt;
      if (effect.growth !== 1) effect.node.scale.set(1 + (effect.growth - 1) * t);

      survivors.push(effect);
    }

    this.effects = survivors;
  }

  private placeAvatar(): void {
    if (!this.avatar) return;
    this.avatar.root.x = this.body.x;
    this.avatar.root.y = this.body.y;
  }

  /**
   * Le cuenta a React lo que cambió, y sólo si cambió.
   *
   * Se compara contra una firma de texto porque el HUD son cinco valores primitivos: si
   * se emitiera cada fotograma, el panel se volvería a renderizar sesenta veces por
   * segundo para escribir los mismos cinco números.
   */
  private publish(): void {
    const hud: FrozenHud = {
      score: this.score,
      remaining: Math.max(0, Math.ceil(DURATION_SECONDS - this.elapsed)),
      chain: this.chain,
      vasos: this.vasos,
      stunned: this.running && this.elapsed * 1000 < this.stunUntil,
    };

    const signature = `${hud.score}:${hud.remaining}:${hud.chain}:${hud.vasos}:${hud.stunned}`;
    if (signature === this.lastHud) return;

    this.lastHud = signature;
    this.options.onHud(hud);
  }

  destroy(): void {
    this.destroyed = true;
    this.running = false;

    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);

    this.avatar?.nameplate.destroy({ children: true });
    // El campus sigue dibujándose detrás. En Pixi 8, `true` también vacía los pools
    // globales: deja batches del otro renderer con textures=null y la sala queda azul.
    this.app.destroy({ removeView: true, releaseGlobalResources: false }, { children: true });
  }
}
